import React, { useMemo, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import 'ag-grid-enterprise'
import { getAgGridSelectionMode, createAgGridIsRowSelectable } from '../../handlers'
import { DEFAULT_ADAPTER_VALUES } from '../BaseAdapter'
import { CustomHeader } from '../../components'
import styles from './AgGridAdapter.module.scss'

/**
 * AG-Grid Adapter for ScoopTable
 * Renders AG-Grid with standardized props interface
 */
const AgGridAdapter = ({
    // Data
    columns,
    rows,
    gridRef,

    // Features
    sorting = {},
    pagination = {},
    selection = {},
    resizable = false,
    reorderable = true,

    // Sizing
    rowHeight = DEFAULT_ADAPTER_VALUES.rowHeight,
    headerHeight = DEFAULT_ADAPTER_VALUES.headerHeight,
    domLayout = DEFAULT_ADAPTER_VALUES.domLayout,
    height,

    // Events
    onRowClick,
    onRowDoubleClick,
    onGridReady,
    onSortChanged,
    onSelectionChanged,

    // Styling
    className,
    style,

    // AG Grid pass-through props
    ...agGridProps
}) => {
    // Default column definition
    const defaultColDef = useMemo(
        () => ({
            sortable: sorting.enabled === true,
            resizable,
            suppressMovable: !reorderable,
            suppressMenu: true,
        }),
        [sorting.enabled, resizable, reorderable]
    )

    // Selection mode
    const rowSelection = useMemo(() => {
        return getAgGridSelectionMode(selection)
    }, [selection])

    // Row selectable callback
    const isRowSelectable = useMemo(() => {
        return createAgGridIsRowSelectable(selection.isRowSelectable)
    }, [selection.isRowSelectable])

    // Handle row click
    const handleRowClicked = useCallback(
        (event) => {
            if (onRowClick) {
                onRowClick(event.data, event)
            }
        },
        [onRowClick]
    )

    // Handle row double click
    const handleRowDoubleClicked = useCallback(
        (event) => {
            if (onRowDoubleClick) {
                onRowDoubleClick(event.data, event)
            }
        },
        [onRowDoubleClick]
    )

    // Handle grid ready
    const handleGridReady = useCallback(
        (event) => {
            if (onGridReady) {
                onGridReady(event)
            }
        },
        [onGridReady]
    )

    // Handle sort changed
    const handleSortChanged = useCallback(
        (event) => {
            if (onSortChanged) {
                onSortChanged(event)
            }
        },
        [onSortChanged]
    )

    // Handle selection changed
    const handleSelectionChanged = useCallback(
        (event) => {
            if (onSelectionChanged) {
                onSelectionChanged(event)
            }
        },
        [onSelectionChanged]
    )

    // Container style
    const containerStyle = useMemo(() => {
        const baseStyle = { width: '100%' }

        if (height) {
            baseStyle.height = height
        } else if (domLayout === 'autoHeight') {
            baseStyle.minHeight = 100
        } else {
            baseStyle.height = '100%'
        }

        return { ...baseStyle, ...style }
    }, [height, domLayout, style])

    // Container class
    const containerClass = useMemo(() => {
        const classes = ['ag-theme-alpine', styles.container]
        if (!resizable) classes.push(styles.noResize)
        if (className) classes.push(className)
        return classes.join(' ')
    }, [className, resizable])

    // Register custom components (only CustomHeader for now)
    const components = useMemo(
        () => ({
            customHeader: CustomHeader,
        }),
        []
    )

    return (
        <div className={containerClass} style={containerStyle}>
            <AgGridReact
                ref={gridRef}
                rowData={rows}
                columnDefs={columns}
                defaultColDef={defaultColDef}
                components={components}
                rowHeight={rowHeight}
                headerHeight={headerHeight}
                domLayout={domLayout}
                // Selection
                rowSelection={rowSelection}
                isRowSelectable={isRowSelectable}
                suppressRowClickSelection={selection.mode === 'none'}
                // Pagination
                pagination={pagination.enabled}
                paginationPageSize={pagination.pageSize}
                paginationPageSizeSelector={pagination.pageSizeOptions}
                suppressPaginationPanel={pagination.customPagination}
                // Events
                onRowClicked={handleRowClicked}
                onRowDoubleClicked={handleRowDoubleClicked}
                onGridReady={handleGridReady}
                onSortChanged={handleSortChanged}
                onSelectionChanged={handleSelectionChanged}
                // UI
                animateRows={true}
                suppressCellFocus={true}
                suppressMenuHide={true}
                suppressMovableColumns={!reorderable}
                // Pass-through props (can override defaults)
                {...agGridProps}
            />
        </div>
    )
}

export default AgGridAdapter
